import { concat, cryptoApi, domain, field, hex, randomField, sameDomain, sha256, unhex, utf8 } from "./encoding";
import { WalletError, type CryptoAdapter, type RecipientAddress, type WalletDomain, type WalletIdentity } from "./types";

const PREFIX = "stv3:";
const BODY_BYTES = 1 + 96 + 32 + 65;
const LABEL = utf8("VANTA_STELLAR_V3_PRIVATE_ADDRESS\0");
export async function encodeAddress(input: RecipientAddress): Promise<string> {
  domain(input);
  field(input.masterPublicKey);
  if (input.version !== 3) throw new WalletError("INVALID_ADDRESS", "Unsupported private address version.");
  const publicKey = unhex(input.encryptionPublicKey, 65);
  if (publicKey[0] !== 4) throw new WalletError("INVALID_ADDRESS", "A canonical uncompressed P-256 key is required.");
  try { await cryptoApi().subtle.importKey("raw", publicKey as BufferSource, { name: "ECDH", namedCurve: "P-256" }, false, []); }
  catch { throw new WalletError("INVALID_ADDRESS", "Invalid recipient encryption key."); }
  const body = concat(new Uint8Array([3]), unhex(input.networkId), unhex(input.poolId), unhex(input.assetId), unhex(input.masterPublicKey), publicKey);
  const checksum = (await sha256(concat(LABEL, body))).slice(0, 8);
  return PREFIX + hex(concat(body, checksum));
}
export async function decodeAddress(value: string, expected: WalletDomain): Promise<RecipientAddress> {
  domain(expected);
  if (typeof value !== "string" || !value.startsWith(PREFIX)) throw new WalletError("INVALID_ADDRESS", "Expected a Stellar V3 private address.");
  let bytes: Uint8Array;
  try { bytes = unhex(value.slice(PREFIX.length), BODY_BYTES + 8); }
  catch { throw new WalletError("INVALID_ADDRESS", "Private address encoding is invalid."); }
  const body = bytes.slice(0, BODY_BYTES);
  const checksum = (await sha256(concat(LABEL, body))).slice(0, 8);
  if (bytes[0] !== 3 || !checksum.every((byte, index) => byte === bytes[BODY_BYTES + index])) throw new WalletError("INVALID_ADDRESS", "Private address checksum or version is invalid.");
  const result: RecipientAddress = {
    version: 3,
    networkId: hex(bytes.slice(1, 33)),
    poolId: hex(bytes.slice(33, 65)),
    assetId: hex(bytes.slice(65, 97)),
    masterPublicKey: hex(bytes.slice(97, 129)),
    encryptionPublicKey: hex(bytes.slice(129, 194)),
  };
  if (!sameDomain(result, expected)) throw new WalletError("WRONG_DOMAIN", "Recipient belongs to a different network, pool, or asset.");
  if (await encodeAddress(result) !== value) throw new WalletError("INVALID_ADDRESS", "Private address is not canonical.");
  return result;
}
export async function createIdentity(input: WalletDomain, adapter: CryptoAdapter): Promise<WalletIdentity> {
  const pinnedDomain = domain(input);
  const spendSecret = randomField(true);
  const nullifyingKey = randomField();
  const masterPublicKey = field(await adapter.deriveMaster({ spendSecret, nullifyingKey }));
  const keyPair = await cryptoApi().subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, ["deriveBits"]);
  const encryptionPublicKey = hex(new Uint8Array(await cryptoApi().subtle.exportKey("raw", keyPair.publicKey)));
  const encryptionPrivateJwk = await cryptoApi().subtle.exportKey("jwk", keyPair.privateKey);
  const recipient: RecipientAddress = { ...pinnedDomain, version: 3, masterPublicKey, encryptionPublicKey };
  return { ...recipient, spendSecret, nullifyingKey, encryptionPrivateJwk, address: await encodeAddress(recipient) };
}
